import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { MultiSelect } from '../ui/multi-select';
import {
  Upload, Search, X, Phone, CheckCircle2, RotateCcw, Pencil, Trash2,
  Loader2, Building2, MapPin, Mail, Contact, ChevronLeft, ChevronRight, History,
  FileSpreadsheet, Users as UsersIcon, PhoneCall,
} from 'lucide-react';
import {
  useClientLeads, useUploadClientLeads, useMarkLeadCalled,
  useUnmarkLeadCalled, useUpdateClientLead, useDeleteClientLead,
  useTriggerLeadCall, useLeadCallLogs, useUpdateCallLogNote,
  useUploadBatches, useUpdateBatchAccess, useDeleteBatch, useUsers,
  useCallStats, useCallHistory,
} from '../../hooks/useQueries';
import { canPlaceCall } from '../../lib/apiBase';

const emptyEditForm = { name: '', phone: '', company_name: '', email: '', location: '' };

const CALL_OUTCOME_LABELS = {
  answered: 'Answered',
  no_answer: 'No Answer',
  not_reachable: 'Not Reachable',
};

const CallLogEntry = ({ leadId, log, autoEdit, onDoneEditing }) => {
  const [editing, setEditing] = useState(!!autoEdit);
  const [draft, setDraft] = useState({ outcome: log.outcome || '', notes: log.notes || '' });
  const updateNoteMutation = useUpdateCallLogNote();

  const save = () => {
    updateNoteMutation.mutate(
      { leadId, callLogId: log.id, outcome: draft.outcome || null, notes: draft.notes || null },
      { onSuccess: () => { setEditing(false); onDoneEditing?.(); } }
    );
  };

  return (
    <div className="text-xs border-b border-border pb-2 last:border-0 last:pb-0">
      <div className="flex items-center justify-between gap-2">
        <span className="font-medium">{log.initiated_by_name || 'Unknown rep'}</span>
        <span className="text-muted-foreground">{new Date(log.created_at).toLocaleString()}</span>
      </div>
      <div className="flex items-center gap-2 mt-0.5">
        {log.outcome ? (
          <Badge
            variant="outline"
            className={
              log.outcome === 'answered'
                ? 'text-emerald-600 border-emerald-500/40'
                : log.outcome === 'no_answer'
                ? 'text-amber-600 border-amber-500/40'
                : 'text-red-600 border-red-500/40'
            }
          >
            {CALL_OUTCOME_LABELS[log.outcome] || log.outcome}
          </Badge>
        ) : (
          <span className="text-muted-foreground italic">Outcome not logged</span>
        )}
        {log.duration != null && <span className="text-muted-foreground">Duration: {log.duration}s</span>}
        {log.recording_url && (
          <a href={log.recording_url} target="_blank" rel="noopener noreferrer" className="text-primary underline">
            Recording
          </a>
        )}
      </div>
      {log.notes && !editing && (
        <p className="mt-1 text-muted-foreground">"{log.notes}"</p>
      )}
      {editing ? (
        <div className="mt-2 space-y-1.5">
          <Select value={draft.outcome} onValueChange={(v) => setDraft((d) => ({ ...d, outcome: v }))}>
            <SelectTrigger className="h-7 text-xs" data-testid={`call-log-outcome-${log.id}`}>
              <SelectValue placeholder="Outcome (optional)" />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(CALL_OUTCOME_LABELS).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Textarea
            className="text-xs"
            rows={2}
            placeholder="What happened on this call?"
            value={draft.notes}
            onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
            data-testid={`call-log-notes-${log.id}`}
          />
          <div className="flex gap-1.5">
            <Button size="sm" className="h-7 text-xs flex-1" onClick={save} disabled={updateNoteMutation.isPending} data-testid={`call-log-save-${log.id}`}>
              {updateNoteMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => { setEditing(false); onDoneEditing?.(); }}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="mt-1 text-primary hover:underline"
          onClick={() => setEditing(true)}
          data-testid={`call-log-edit-${log.id}`}
        >
          {log.outcome || log.notes ? 'Edit note' : 'Add note'}
        </button>
      )}
    </div>
  );
};

const CallHistoryButton = ({ leadId, open, onOpenChange, autoEditLogId, onAutoEditHandled }) => {
  const { data: logs = [], isLoading } = useLeadCallLogs(leadId, { enabled: open });

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <Button
          size="icon"
          variant="ghost"
          className="text-muted-foreground hover:text-foreground"
          title="Call history"
          data-testid={`client-lead-history-${leadId}`}
        >
          <History className="w-4 h-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        collisionPadding={12}
        className="w-[min(20rem,calc(100vw-2rem))]"
        data-testid={`client-lead-history-popover-${leadId}`}
      >
        <p className="text-xs font-semibold mb-2">Call History</p>
        {isLoading ? (
          <div className="flex justify-center py-3">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        ) : logs.length === 0 ? (
          <p className="text-xs text-muted-foreground">No calls yet.</p>
        ) : (
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {logs.map((log) => (
              <CallLogEntry
                key={log.id}
                leadId={leadId}
                log={log}
                autoEdit={log.id === autoEditLogId}
                onDoneEditing={onAutoEditHandled}
              />
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

const CALL_DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'this_week', label: 'This Week' },
  { id: 'this_month', label: 'This Month' },
  { id: 'custom', label: 'Custom Range' },
];

const isoDate = (d) => d.toISOString().slice(0, 10);

const computeCallDateRange = (preset) => {
  const today = new Date();
  const start = new Date(today);
  switch (preset) {
    case 'yesterday':
      start.setDate(start.getDate() - 1);
      return { from_date: isoDate(start), to_date: isoDate(start) };
    case 'this_week':
      start.setDate(today.getDate() - today.getDay());
      return { from_date: isoDate(start), to_date: isoDate(today) };
    case 'this_month':
      return { from_date: isoDate(new Date(today.getFullYear(), today.getMonth(), 1)), to_date: isoDate(today) };
    case 'today':
    default:
      return { from_date: isoDate(today), to_date: isoDate(today) };
  }
};

// Drill-down behind the summary: individual call_logs entries for the
// selected period, optionally narrowed to one rep (click a breakdown row).
const CallHistoryDialog = ({ open, onOpenChange, range, rep, page, onPageChange }) => {
  const { data, isLoading } = useCallHistory(
    { ...range, sales_user_id: rep?.user_id, page, page_size: 20 },
    { enabled: open }
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Call Details{rep ? ` — ${rep.name}` : ''}</DialogTitle>
          <DialogDescription>
            {range.from_date === range.to_date ? range.from_date : `${range.from_date} – ${range.to_date}`}
            {data ? ` · ${data.total} call${data.total !== 1 ? 's' : ''}` : ''}
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto space-y-2" data-testid="client-database-call-history-list">
          {isLoading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : data?.items?.length ? (
            data.items.map((log) => (
              <div key={log.id} className="text-xs border-b border-border pb-2 last:border-0">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">
                    {log.lead_name || 'Unknown lead'}{log.lead_company ? ` · ${log.lead_company}` : ''}
                  </span>
                  <span className="text-muted-foreground shrink-0">{new Date(log.created_at).toLocaleString()}</span>
                </div>
                <div className="flex flex-wrap items-center gap-2 mt-0.5 text-muted-foreground">
                  <span>By {log.initiated_by_name || 'Unknown rep'}</span>
                  {log.lead_phone && <span>· {log.lead_phone}</span>}
                  {log.outcome && (
                    <Badge variant="outline" className="text-[10px]">
                      {CALL_OUTCOME_LABELS[log.outcome] || log.outcome}
                    </Badge>
                  )}
                </div>
                {log.notes && <p className="text-muted-foreground mt-0.5 italic">"{log.notes}"</p>}
              </div>
            ))
          ) : (
            <p className="text-xs text-muted-foreground text-center py-6">No calls in this period.</p>
          )}
        </div>
        {data && data.total_pages > 1 && (
          <div className="flex items-center justify-between pt-2">
            <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs text-muted-foreground">Page {page} of {data.total_pages}</span>
            <Button size="sm" variant="outline" disabled={page >= data.total_pages} onClick={() => onPageChange(page + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Admin/Hub Manager only — total calls for the selected period up front
// (defaults to today), with the per-rep breakdown and full call log tucked
// behind "View Breakdown" so the headline number stays the focus. Counts
// come live from call_logs (see GET /client-leads/call-stats and
// /call-history) so they're always accurate and correctly attributed to
// whoever placed each call, and update as soon as the date range changes.
const CallActivityPanel = () => {
  const [preset, setPreset] = useState('today');
  const [range, setRange] = useState(() => computeCallDateRange('today'));
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyRep, setHistoryRep] = useState(null);
  const [historyPage, setHistoryPage] = useState(1);

  const { data: stats, isLoading } = useCallStats(range);

  const applyPreset = (id) => {
    setPreset(id);
    if (id !== 'custom') setRange(computeCallDateRange(id));
  };

  const openHistory = (rep) => {
    setHistoryRep(rep);
    setHistoryPage(1);
    setHistoryOpen(true);
  };

  const periodLabel = preset === 'today' ? 'today'
    : preset === 'yesterday' ? 'yesterday'
    : range.from_date === range.to_date ? `on ${range.from_date}`
    : `${range.from_date} to ${range.to_date}`;

  return (
    <Card data-testid="client-database-call-stats">
      <CardContent className="p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
              <PhoneCall className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold">Sales Call Activity</p>
              <p className="text-xs text-muted-foreground truncate">Total calls {periodLabel}</p>
            </div>
          </div>

          <p className="text-3xl font-bold" data-testid="client-database-call-stats-total">
            {isLoading ? <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /> : (stats?.total_calls ?? 0)}
          </p>

          <div className="flex items-center gap-2 sm:ml-auto flex-wrap">
            <Select value={preset} onValueChange={applyPreset}>
              <SelectTrigger className="w-[140px]" data-testid="client-database-call-stats-preset">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CALL_DATE_PRESETS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {preset === 'custom' && (
              <>
                <Input
                  type="date"
                  className="w-[140px]"
                  value={range.from_date}
                  onChange={(e) => setRange((r) => ({ ...r, from_date: e.target.value }))}
                  data-testid="client-database-call-stats-from"
                />
                <span className="text-xs text-muted-foreground">to</span>
                <Input
                  type="date"
                  className="w-[140px]"
                  value={range.to_date}
                  onChange={(e) => setRange((r) => ({ ...r, to_date: e.target.value }))}
                  data-testid="client-database-call-stats-to"
                />
              </>
            )}
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowBreakdown((v) => !v)}
              data-testid="client-database-call-stats-toggle"
            >
              {showBreakdown ? 'Hide' : 'View'} Breakdown
            </Button>
          </div>
        </div>

        {showBreakdown && (
          <div className="mt-4 pt-4 border-t border-border space-y-1.5">
            {isLoading ? (
              <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>
            ) : stats?.breakdown?.length ? (
              <>
                {stats.breakdown.map((rep) => {
                  const pct = stats.total_calls > 0 ? Math.round((rep.call_count / stats.total_calls) * 100) : 0;
                  return (
                    <button
                      key={rep.user_id}
                      type="button"
                      onClick={() => openHistory(rep)}
                      className="w-full flex items-center gap-3 text-sm text-left hover:bg-muted/50 rounded px-1.5 py-1 -mx-1.5 transition-colors"
                      title="View this rep's calls for the period"
                      data-testid={`client-database-call-stats-rep-${rep.user_id}`}
                    >
                      <span className="w-32 sm:w-40 truncate shrink-0">{rep.name}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="w-8 text-right font-medium shrink-0">{rep.call_count}</span>
                    </button>
                  );
                })}
                <Button
                  variant="link"
                  size="sm"
                  className="px-0 h-auto mt-1"
                  onClick={() => openHistory(null)}
                  data-testid="client-database-call-stats-view-all"
                >
                  View call details for this period →
                </Button>
              </>
            ) : (
              <p className="text-xs text-muted-foreground">No Sales reps found.</p>
            )}
          </div>
        )}
      </CardContent>

      <CallHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        range={range}
        rep={historyRep}
        page={historyPage}
        onPageChange={setHistoryPage}
      />
    </Card>
  );
};

const BatchAccessRow = ({ batch, salesOptions }) => {
  const updateAccessMutation = useUpdateBatchAccess();
  const [selected, setSelected] = useState(batch.allowed_sales_ids || []);
  const dirty = JSON.stringify([...selected].sort()) !== JSON.stringify([...(batch.allowed_sales_ids || [])].sort());

  return (
    <div className="flex items-center gap-2">
      <MultiSelect
        options={salesOptions}
        selected={selected}
        onChange={setSelected}
        placeholder="Sales Persons"
        allLabel="No access granted"
        triggerClassName="w-[220px]"
        data-testid={`batch-access-select-${batch.id}`}
      />
      {dirty && (
        <Button
          size="sm"
          onClick={() => updateAccessMutation.mutate({ batchId: batch.id, salesUserIds: selected })}
          disabled={updateAccessMutation.isPending}
          data-testid={`batch-access-save-${batch.id}`}
        >
          {updateAccessMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
        </Button>
      )}
    </div>
  );
};

const UploadBatchesView = () => {
  const { data: batches = [], isLoading } = useUploadBatches();
  const { data: allUsers = [] } = useUsers();
  const deleteBatchMutation = useDeleteBatch();
  const [deleteTarget, setDeleteTarget] = useState(null);

  const salesOptions = useMemo(
    () => allUsers.filter((u) => u.role === 'sales').map((u) => ({ value: u.id, label: u.name || u.email })),
    [allUsers]
  );

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteBatchMutation.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <Card>
        <CardContent className="p-10 text-center text-muted-foreground">
          <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p>No files uploaded yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="upload-batches-list">
      {batches.map((batch) => (
        <Card key={batch.id} data-testid={`upload-batch-${batch.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <p className="font-medium text-sm flex items-center gap-1.5">
                  <FileSpreadsheet className="w-4 h-4 text-primary shrink-0" />
                  {batch.filename}
                </p>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                  <span>{batch.row_count} client{batch.row_count !== 1 ? 's' : ''}</span>
                  {batch.uploaded_by_name && <span>Uploaded by {batch.uploaded_by_name}</span>}
                  <span>{new Date(batch.uploaded_at).toLocaleString()}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <UsersIcon className="w-4 h-4 text-muted-foreground" />
                <BatchAccessRow batch={batch} salesOptions={salesOptions} />
                <button
                  onClick={() => setDeleteTarget(batch)}
                  className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                  title="Delete this uploaded file"
                  data-testid={`upload-batch-delete-${batch.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Uploaded File?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove "{deleteTarget?.filename}" and all {deleteTarget?.row_count} client record(s) it added. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90" data-testid="upload-batch-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const ClientDatabaseTab = ({ user }) => {
  const isManager = user?.role === 'admin' || user?.role === 'hub_manager';

  const { data: leads = [], isLoading } = useClientLeads();
  const { data: allUsers = [] } = useUsers({ enabled: isManager });
  const uploadMutation = useUploadClientLeads();
  const callMutation = useTriggerLeadCall();
  const markCalledMutation = useMarkLeadCalled();
  const unmarkCalledMutation = useUnmarkLeadCalled();
  const updateMutation = useUpdateClientLead();
  const deleteMutation = useDeleteClientLead();
  const grantAccessMutation = useUpdateBatchAccess();

  const fileInputRef = useRef(null);
  const [subView, setSubView] = useState('clients'); // clients | uploads — manager only
  const [search, setSearch] = useState('');
  const [calledFilter, setCalledFilter] = useState('all'); // all | called | not_called
  const [uploadSummary, setUploadSummary] = useState(null);
  const [grantSelection, setGrantSelection] = useState([]);
  const [noteDraft, setNoteDraft] = useState('');
  const [editingLead, setEditingLead] = useState(null);
  const [editForm, setEditForm] = useState(emptyEditForm);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Which lead's Call History popover is open, and which entry in it (if
  // any) should open straight into "add note" mode — set right after a call
  // is placed, so the rep is dropped into logging the outcome/notes.
  const [openHistoryLeadId, setOpenHistoryLeadId] = useState(null);
  const [autoEditCallLogId, setAutoEditCallLogId] = useState(null);

  const salesOptions = useMemo(
    () => allUsers.filter((u) => u.role === 'sales').map((u) => ({ value: u.id, label: u.name || u.email })),
    [allUsers]
  );

  const filteredLeads = useMemo(() => {
    let result = leads;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      result = result.filter((l) =>
        l.name?.toLowerCase().includes(q) ||
        l.company_name?.toLowerCase().includes(q) ||
        l.phone?.toLowerCase().includes(q) ||
        l.email?.toLowerCase().includes(q) ||
        l.location?.toLowerCase().includes(q)
      );
    }
    if (calledFilter === 'called') result = result.filter((l) => l.called);
    else if (calledFilter === 'not_called') result = result.filter((l) => !l.called);
    return result;
  }, [leads, search, calledFilter]);

  // Reset to page 1 whenever the filtered set changes underneath the pager,
  // otherwise a filter change can strand the view on a now-empty page.
  useEffect(() => {
    setPage(1);
  }, [search, calledFilter, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filteredLeads.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pagedLeads = filteredLeads.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const getPageNumbers = (current, total) => {
    const delta = 2;
    const pages = [];
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        pages.push(i);
      }
    }
    const withGaps = [];
    let prev;
    pages.forEach((p) => {
      if (prev && p - prev > 1) withGaps.push('...');
      withGaps.push(p);
      prev = p;
    });
    return withGaps;
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    uploadMutation.mutate(file, {
      onSuccess: (data) => { setUploadSummary(data); setGrantSelection([]); },
    });
  };

  const openEdit = (lead) => {
    setEditingLead(lead);
    setEditForm({
      name: lead.name || '',
      phone: lead.phone || '',
      company_name: lead.company_name || '',
      email: lead.email || '',
      location: lead.location || '',
    });
  };

  const saveEdit = () => {
    updateMutation.mutate(
      { leadId: editingLead.id, updates: editForm },
      { onSuccess: () => setEditingLead(null) }
    );
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
  };

  return (
    <div className="space-y-6" data-testid="client-database-tab">
      {isManager && (
        <div className="flex items-center gap-1 border-b border-border">
          <button
            onClick={() => setSubView('clients')}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              subView === 'clients' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="client-database-subview-clients"
          >
            Clients
          </button>
          <button
            onClick={() => setSubView('uploads')}
            className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              subView === 'uploads' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="client-database-subview-uploads"
          >
            Uploaded Files
          </button>
        </div>
      )}

      {isManager && subView === 'uploads' ? (
        <UploadBatchesView />
      ) : (
      <>
      {isManager && <CallActivityPanel />}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 min-w-0">
        <div className="flex items-center gap-2 sm:gap-3 flex-1 w-full sm:w-auto min-w-0">
          <div className="relative flex-1 min-w-0 sm:max-w-sm">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-9"
              placeholder="Search name, company, phone, email, location…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="client-database-search"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Select value={calledFilter} onValueChange={setCalledFilter}>
            <SelectTrigger className="w-[112px] sm:w-[150px] shrink-0" data-testid="client-database-called-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              <SelectItem value="called">Called</SelectItem>
              <SelectItem value="not_called">Not Called</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {isManager && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleFileSelected}
              data-testid="client-database-file-input"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadMutation.isPending}
              className="bg-primary hover:bg-primary/90 shrink-0"
              data-testid="client-database-upload-btn"
            >
              {uploadMutation.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              Upload Excel
            </Button>
          </>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        {filteredLeads.length === 0
          ? `0 of ${leads.length} client${leads.length !== 1 ? 's' : ''}`
          : `Showing ${(currentPage - 1) * pageSize + 1}-${Math.min(currentPage * pageSize, filteredLeads.length)} of ${filteredLeads.length} client${filteredLeads.length !== 1 ? 's' : ''} (${leads.length} total)`}
      </p>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : filteredLeads.length === 0 ? (
        <Card>
          <CardContent className="p-10 text-center text-muted-foreground">
            <Contact className="w-10 h-10 mx-auto mb-3 opacity-50" />
            <p>
              {leads.length > 0
                ? 'No records match your search/filter.'
                : isManager
                  ? 'No client records yet.'
                  : 'No clients have been shared with you yet — ask your Admin to grant access.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {pagedLeads.map((lead) => (
            <Card key={lead.id} data-testid={`client-lead-${lead.id}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium text-sm">{lead.name}</p>
                      {lead.called ? (
                        <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30" data-testid={`client-lead-called-badge-${lead.id}`}>
                          Called
                        </Badge>
                      ) : (
                        <Badge variant="outline">Not Called</Badge>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-xs text-muted-foreground">
                      {lead.company_name && (
                        <span className="flex items-center gap-1"><Building2 className="w-3.5 h-3.5" />{lead.company_name}</span>
                      )}
                      {lead.phone && (
                        <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{lead.phone}</span>
                      )}
                      {lead.email && (
                        <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5" />{lead.email}</span>
                      )}
                      {lead.location && (
                        <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{lead.location}</span>
                      )}
                    </div>
                    {lead.extra_fields && Object.keys(lead.extra_fields).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {Object.entries(lead.extra_fields).map(([k, v]) => (
                          <span key={k} className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                            {k}: {v}
                          </span>
                        ))}
                      </div>
                    )}
                    {lead.called && (
                      <p className="text-[11px] text-muted-foreground mt-1.5">
                        Called by <span className="font-medium">{lead.called_by}</span>
                        {lead.called_at && ` on ${new Date(lead.called_at).toLocaleString()}`}
                        {lead.call_notes && <> — "{lead.call_notes}"</>}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    {lead.phone && canPlaceCall() && (
                      <Button
                        size="icon"
                        className="w-10 h-10 bg-emerald-500 hover:bg-emerald-600 text-white shadow-sm"
                        onClick={() => callMutation.mutate(lead.id, {
                          onSuccess: (data) => {
                            if (data?.call_log_id) {
                              setOpenHistoryLeadId(lead.id);
                              setAutoEditCallLogId(data.call_log_id);
                            }
                          },
                        })}
                        disabled={callMutation.isPending}
                        title="Call — opens your phone's dialer with the number ready, so the customer sees your own number"
                        data-testid={`client-lead-call-${lead.id}`}
                      >
                        {callMutation.isPending && callMutation.variables === lead.id ? (
                          <Loader2 className="w-5 h-5 animate-spin" />
                        ) : (
                          <Phone className="w-5 h-5" />
                        )}
                      </Button>
                    )}
                    {lead.phone && (
                      <CallHistoryButton
                        leadId={lead.id}
                        open={openHistoryLeadId === lead.id}
                        onOpenChange={(next) => {
                          setOpenHistoryLeadId(next ? lead.id : null);
                          if (!next) setAutoEditCallLogId(null);
                        }}
                        autoEditLogId={openHistoryLeadId === lead.id ? autoEditCallLogId : undefined}
                        onAutoEditHandled={() => setAutoEditCallLogId(null)}
                      />
                    )}

                    {isManager && (
                      <>
                        <button
                          onClick={() => openEdit(lead)}
                          className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted/50"
                          title="Edit"
                          data-testid={`client-lead-edit-${lead.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeleteTarget(lead)}
                          className="p-2 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                          title="Delete"
                          data-testid={`client-lead-delete-${lead.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}

                    {lead.called ? (
                      isManager && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="ml-auto"
                          onClick={() => unmarkCalledMutation.mutate(lead.id)}
                          disabled={unmarkCalledMutation.isPending}
                          title="Undo — mark as not called"
                          data-testid={`client-lead-unmark-${lead.id}`}
                        >
                          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                          Undo
                        </Button>
                      )
                    ) : (
                      <Popover onOpenChange={(open) => open && setNoteDraft('')}>
                        <PopoverTrigger asChild>
                          <Button size="sm" className="ml-auto" data-testid={`client-lead-mark-called-${lead.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                            Mark Called
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent align="end" collisionPadding={12} className="w-[min(18rem,calc(100vw-2rem))]">
                          <Label className="text-xs">Note (optional)</Label>
                          <Textarea
                            className="mt-1 text-sm"
                            rows={3}
                            placeholder="e.g. Interested, follow up next week"
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                            data-testid={`client-lead-note-${lead.id}`}
                          />
                          <Button
                            size="sm"
                            className="w-full mt-2"
                            onClick={() => markCalledMutation.mutate({ leadId: lead.id, notes: noteDraft })}
                            disabled={markCalledMutation.isPending}
                            data-testid={`client-lead-confirm-called-${lead.id}`}
                          >
                            Confirm Called
                          </Button>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-3" data-testid="client-database-pagination">
          <div className="flex items-center gap-1 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              data-testid="client-database-prev-page"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Prev
            </Button>
            {getPageNumbers(currentPage, totalPages).map((p, idx) =>
              p === '...' ? (
                <span key={`gap-${idx}`} className="px-1.5 text-sm text-muted-foreground">...</span>
              ) : (
                <Button
                  key={p}
                  variant={p === currentPage ? 'default' : 'outline'}
                  size="sm"
                  className="w-8 h-8 p-0"
                  onClick={() => setPage(p)}
                  data-testid={`client-database-page-${p}`}
                >
                  {p}
                </Button>
              )
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              data-testid="client-database-next-page"
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
            <SelectTrigger className="w-[130px]" data-testid="client-database-page-size">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10 per page</SelectItem>
              <SelectItem value="25">25 per page</SelectItem>
              <SelectItem value="50">50 per page</SelectItem>
              <SelectItem value="100">100 per page</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Upload summary dialog */}
      <Dialog open={!!uploadSummary} onOpenChange={() => setUploadSummary(null)}>
        <DialogContent data-testid="client-database-upload-summary">
          <DialogHeader>
            <DialogTitle>Import Complete</DialogTitle>
            <DialogDescription>{uploadSummary?.total_rows_in_file || 0} data row(s) processed</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 text-sm">
            <p className="text-emerald-600"><strong>{uploadSummary?.inserted || 0}</strong> new client(s) added</p>
            <p className="text-amber-600"><strong>{uploadSummary?.duplicates_skipped || 0}</strong> duplicate(s) skipped (already in the database)</p>
            {uploadSummary?.invalid_rows?.length > 0 && (
              <div>
                <p className="text-red-600 mb-1"><strong>{uploadSummary.invalid_rows.length}</strong> row(s) skipped — invalid:</p>
                <div className="max-h-40 overflow-y-auto rounded-lg border border-border p-2 space-y-1">
                  {uploadSummary.invalid_rows.map((r, i) => (
                    <p key={i} className="text-xs text-muted-foreground">Row {r.row}: {r.reason}</p>
                  ))}
                </div>
              </div>
            )}
            {uploadSummary?.batch_id && (
              <div className="pt-2 border-t border-border">
                <Label className="text-xs">Grant access to Sales Persons</Label>
                <p className="text-[11px] text-muted-foreground mb-1.5">Nobody can see these clients until you pick who can.</p>
                <MultiSelect
                  options={salesOptions}
                  selected={grantSelection}
                  onChange={setGrantSelection}
                  placeholder="Sales Persons"
                  allLabel="No access granted"
                  triggerClassName="w-full"
                  data-testid="upload-summary-grant-access-select"
                />
              </div>
            )}
          </div>
          <DialogFooter>
            {uploadSummary?.batch_id && (
              <Button
                variant="outline"
                onClick={() => grantAccessMutation.mutate(
                  { batchId: uploadSummary.batch_id, salesUserIds: grantSelection },
                  { onSuccess: () => setUploadSummary(null) }
                )}
                disabled={grantSelection.length === 0 || grantAccessMutation.isPending}
                data-testid="upload-summary-grant-access-btn"
              >
                {grantAccessMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Grant Access
              </Button>
            )}
            <Button onClick={() => setUploadSummary(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editingLead} onOpenChange={(open) => !open && setEditingLead(null)}>
        <DialogContent data-testid="client-lead-edit-modal">
          <DialogHeader>
            <DialogTitle>Edit Client</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div>
              <Label>Phone</Label>
              <Input value={editForm.phone} onChange={(e) => setEditForm((p) => ({ ...p, phone: e.target.value }))} />
            </div>
            <div>
              <Label>Company Name</Label>
              <Input value={editForm.company_name} onChange={(e) => setEditForm((p) => ({ ...p, company_name: e.target.value }))} />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={editForm.email} onChange={(e) => setEditForm((p) => ({ ...p, email: e.target.value }))} />
            </div>
            <div>
              <Label>Location</Label>
              <Input value={editForm.location} onChange={(e) => setEditForm((p) => ({ ...p, location: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingLead(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={updateMutation.isPending} data-testid="client-lead-edit-save">
              {updateMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Client Record?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove "{deleteTarget?.name}" from the Client Database. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive hover:bg-destructive/90" data-testid="client-lead-delete-confirm">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </>
      )}
    </div>
  );
};

export default ClientDatabaseTab;
